import { useCallback, useRef, useState } from "react";

interface UseRangeSliderProps {
  min: number;
  max: number;
  minValue: number;
  maxValue: number;
  onMinChange: (value: number) => void;
  onMaxChange: (value: number) => void;
  onMinUIUpdate?: (value: number) => void;
  onMaxUIUpdate?: (value: number) => void;
  step?: number;
}

export const useRangeSlider = ({
  min,
  max,
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  onMinUIUpdate,
  onMaxUIUpdate,
  step = 1,
}: UseRangeSliderProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState<"min" | "max" | null>(null);

  const calculateValueFromPosition = useCallback(
    (clientX: number): number => {
      if (!trackRef.current) return min;

      const rect = trackRef.current.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const rawValue = min + percent * (max - min);
      const steppedValue = Math.round(rawValue / step) * step;

      // Fix floating point precision issues by rounding to the step's decimal places
      const decimalPlaces = step < 1 ? Math.ceil(-Math.log10(step)) : 0;
      const fixedValue = Number(steppedValue.toFixed(decimalPlaces));

      return Math.max(min, Math.min(max, fixedValue));
    },
    [min, max, step],
  );

  const handleMinThumbMouseDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      setIsDragging("min");

      let lastMinValue = minValue;
      let lastMaxValue = maxValue;

      const startedOverlapping = minValue === maxValue;

      const handleMove = (moveEvent: MouseEvent | TouchEvent) => {
        const clientX = "touches" in moveEvent ? moveEvent.touches[0].clientX : moveEvent.clientX;
        const newValue = calculateValueFromPosition(clientX);
        if (newValue <= lastMaxValue) {
          lastMinValue = newValue;
          // Update UI immediately (optimistic update)
          onMinUIUpdate?.(newValue);
        } else if (startedOverlapping && newValue > lastMaxValue) {
          // Push the max thumb right when starting from an overlapping position
          lastMaxValue = newValue;
          onMaxUIUpdate?.(newValue);
        }
      };

      const handleUp = () => {
        setIsDragging(null);
        // Call the form update callback only when dragging ends
        onMinChange(lastMinValue);
        // If max was pushed during drag, commit that change too
        if (lastMaxValue !== maxValue) {
          onMaxChange(lastMaxValue);
        }
        document.removeEventListener("mousemove", handleMove);
        document.removeEventListener("mouseup", handleUp);
        document.removeEventListener("touchmove", handleMove);
        document.removeEventListener("touchend", handleUp);
      };

      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleUp);
      document.addEventListener("touchmove", handleMove);
      document.addEventListener("touchend", handleUp);
    },
    [calculateValueFromPosition, minValue, maxValue, onMinChange, onMaxChange, onMinUIUpdate, onMaxUIUpdate],
  );

  const handleMaxThumbMouseDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      setIsDragging("max");

      // Track the last value during dragging
      let lastValue = maxValue;

      const handleMove = (moveEvent: MouseEvent | TouchEvent) => {
        const clientX = "touches" in moveEvent ? moveEvent.touches[0].clientX : moveEvent.clientX;
        const newValue = calculateValueFromPosition(clientX);
        if (newValue >= minValue) {
          lastValue = newValue;
          // Update UI immediately (optimistic update)
          onMaxUIUpdate?.(newValue);
        }
      };

      const handleUp = () => {
        setIsDragging(null);
        // Call the form update callback only when dragging ends
        if (lastValue >= minValue) {
          onMaxChange(lastValue);
        }
        document.removeEventListener("mousemove", handleMove);
        document.removeEventListener("mouseup", handleUp);
        document.removeEventListener("touchmove", handleMove);
        document.removeEventListener("touchend", handleUp);
      };

      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleUp);
      document.addEventListener("touchmove", handleMove);
      document.addEventListener("touchend", handleUp);
    },
    [calculateValueFromPosition, minValue, maxValue, onMaxChange, onMaxUIUpdate],
  );

  const getMinPercent = useCallback(() => {
    return ((minValue - min) / (max - min)) * 100;
  }, [minValue, min, max]);

  const getMaxPercent = useCallback(() => {
    return ((maxValue - min) / (max - min)) * 100;
  }, [maxValue, min, max]);

  return {
    trackRef,
    isDragging,
    handleMinThumbMouseDown,
    handleMaxThumbMouseDown,
    getMinPercent,
    getMaxPercent,
  };
};
